import {
  backButton,
  init as initSDK,
  initData,
  miniApp,
  postEvent,
  swipeBehavior,
  themeParams,
  viewport,
} from '@tma.js/sdk-react';

let initialization: Promise<void> | undefined;
const cleanups: VoidFunction[] = [];
const mountController = new AbortController();

export const disposeTelegram = (): void => {
  mountController.abort();
  cleanups.reverse().forEach((cleanup) => cleanup());
  cleanups.length = 0;
  backButton.unmount();
  swipeBehavior.unmount();
  miniApp.unmount();
  themeParams.unmount();
};

const initialize = (): void => {
  // SDK debug logging includes launch/auth payloads. Keep it disabled even when
  // the app's development console is enabled.
  cleanups.push(initSDK());
  initData.restore();
  themeParams.mount();
  // Mount restores cached theme on a page reload. Ask the host for its current
  // palette so theme changes (and development overrides) replace that cache.
  postEvent('web_app_request_theme');
  cleanups.push(themeParams.bindCssVars());
  miniApp.mount.ifAvailable();
  backButton.mount.ifAvailable();
  swipeBehavior.mount.ifAvailable();
  // Sheet gesture locks are transient; do not restore a lock from a page reload.
  swipeBehavior.enableVertical.ifAvailable();

  const syncNativeColors = (): void => {
    miniApp.setHeaderColor.ifAvailable('secondary_bg_color');
    const color = themeParams.secondaryBgColor() ?? themeParams.bgColor();
    if (color) {
      miniApp.setBgColor.ifAvailable(color);
      miniApp.setBottomBarColor.ifAvailable(color);
    }
  };
  syncNativeColors();
  cleanups.push(themeParams.state.sub(syncNativeColors));

  // Request expansion immediately; viewport measurements can arrive after render.
  viewport.expand.ifAvailable();
  void mountViewport();
};

const mountViewport = async (): Promise<void> => {
  try {
    // Some Telegram clients fail to respond to viewport/safe-area requests.
    // Run independently of initialization so the first render uses CSS viewport
    // and env(safe-area-inset-*) defaults while the host responds.
    if (viewport.mount.isAvailable()) {
      await viewport.mount({ timeout: 1500, abortSignal: mountController.signal });
      if (!mountController.signal.aborted) cleanups.push(viewport.bindCssVars());
    }
  } catch {
    if (import.meta.env.DEV && !mountController.signal.aborted)
      console.info('Telegram viewport is unavailable; using browser defaults.');
  }
};

export const initializeTelegram = (): Promise<void> => {
  initialization ??= Promise.resolve().then(initialize);
  return initialization;
};

export const notifyTelegramReady = (): void => {
  miniApp.ready.ifAvailable();
};

if (import.meta.hot) import.meta.hot.dispose(disposeTelegram);
